/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2019, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
/**
 * @class DashboardState
 * @hideconstructor
 * @classdesc This class provides APIs for dashboard avtivity state
 */
define([
	'underscore',
	'./DashboardStateAPI',
	'../../../api/DashboardAPI',
	'../../../lib/@waca/dashboard-common/dist/api/impl/CallbackInvoker',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory'
], (_, DashboardStateAPI, DashboardAPI, CallbackInvoker, APIFactory) => {

	class DashboardState extends CallbackInvoker{
		static ON_DASHBOARD_STATE_CHANGE = 'dashboardStateChange'; // TODO: Remove this when we remove this._active
		static UI_CHANGE = 'dashboardStateUiChange';

		constructor({ features }) {
			super({ logger: features.Logger });

			this._dashboard = features.API;
			this._api = APIFactory.createAPI(this, [DashboardStateAPI]);
			this._features = features;
			this._active = false;

			this._uiState = {
				// active: false, // TODO: Implement me and remove the this._active
				dirty: false,
				authoring: false,
				eventGroups: false,
				fullScreen: false,
				selectionProperties: false,
				focus: false,
				sidePanel: {
					isOpen: false,
					current: null
				}
			};
		}

		getLifeCycleHandlers() {
			return [{
				name: 'post:dashboard.initialize',
				action: this.postDashboardInitialize.bind(this)
			}];
		}

		postDashboardInitialize() {
			const controller = this._dashboard.getFeature('InteractionController.internal');
			this._modeChangeHandler = controller.eventRouter.on('mode:change', this.authoringModeChanged.bind(this));
			this._uiState.authoring = (this._dashboard.getMode() !== DashboardAPI.MODES.VIEW);
			return Promise.resolve();
		}

		getAPI() {
			return this._api;
		}

		destroy() {
			this._uiState = null;
			this._api = null;
			this._active = null;
			if (this._modeChangeHandler) {
				this._modeChangeHandler.remove();
				this._modeChangeHandler = null;
			}
		}

		/**
		 * @implements DashboardStateAPI#setDirty
		 * @param {boolean} dirty
		 */
		setDirty(dirty) {
			this._uiState.dirty = dirty;
			this._dashboard.setDirty(dirty);
			this._invokeUiChangeCallback();
		}

		/**
		 * @implements DashboardStateAPI#onUiStateChange
		 * @param {function} callback
		 */
		onUiStateChange(callback) {
			this.registerCallback(DashboardState.UI_CHANGE, callback);
		}

		/**
		 * @implements DashboardStateAPI#offUiStateChange
		 * @param {function} callback
		 */
		offUiStateChange(callback) {
			this.removeCallback(DashboardState.UI_CHANGE, callback);
		}

		_invokeUiChangeCallback() {
			this.invokeCallbacks(DashboardState.UI_CHANGE, this._uiState);
		}

		/**
		 * @implements DashboardStateAPI#getUiState
		 * @returns {Object} the ui state object @see
		 */
		getUiState() {
			return this._uiState;
		}

		/**
		 * @implements DashboardStateAPI#updateStates
		 */
		updateUiState({ stateChange }) {
			Object.keys(stateChange).forEach((stateName) => {
				this._uiState[stateName] = stateChange[stateName];
			});
			this._invokeUiChangeCallback();
		}

		/**
		 * @implements DashboardStateAPI#setAuthoring
		 * TODO: the selectionProperties should not be set in the setAuthoring. This
		 * needs to be removed.
		 */
		setAuthoring(flag) {
			const renderer =  this._features['Renderer.internal'];
			if (renderer && renderer.isMobile() && !!flag) {
				this._logger.warn('Authoring mode is not allowed in a mobile layout.');
				return;
			}

			this._uiState.authoring = !!flag;

			if (this._uiState.authoring === true) {
				this._dashboard.setMode(DashboardAPI.MODES.EDIT);
			} else {
				this._dashboard.setMode(DashboardAPI.MODES.VIEW);
				this._uiState.eventGroups = false;
				this._uiState.selectionProperties = false;
			}
		}

		authoringModeChanged(event) {
			this._uiState.authoring = !!(event.authoring);

			this._invokeUiChangeCallback();
		}

		/**
		 * @implements DashboardStateAPI#setSelectionProperties
		 */
		setSelectionProperties(flag) {
			this._uiState.selectionProperties = !!flag;
			this._invokeUiChangeCallback();
		}

		setSidePanelOpen(state) {
			this._uiState.sidePanel.isOpen = !!state;
			if (!state) {
				// closing the panel implies there are no current view
				// this avoids the change callbacks being called twice when closing the side panel
				this._uiState.sidePanel.current = null;
			}
			this._invokeUiChangeCallback();
		}

		setSidePanelCurrentView(view) {
			this._uiState.sidePanel.current = view;
			this._invokeUiChangeCallback();
		}

		/**
		 * @implements DashboardStateAPI#setEventGroups
		 * TODO: the selectionProperties should not be set in the setEventGroups. This
		 * needs to be removed.
		 */
		setEventGroups(flag) {
			if (this._uiState.authoring === true) {
				this._uiState.eventGroups = !!flag;

				let newMode;
				if (this._uiState.eventGroups === true) {
					newMode = DashboardAPI.MODES.EDIT_GROUP;
					this._uiState.selectionProperties = false;
				} else {
					newMode = DashboardAPI.MODES.EDIT;
				}
				return this._dashboard.setMode(newMode).then(() => {
					this._invokeUiChangeCallback();
				});
			} else {
				this._logger.info('The dashboard is not in the correct mode to perform the EventGroups action');
			}
		}

		setFullScreen(flag) {
			this._uiState.fullScreen = !!flag;
			this._invokeUiChangeCallback();
		}

		/* TODO: The following should be removed/updated to use uiState object */
		/**
		 * @implements DashboardStateAPI#isActive
		 */
		isActive() {
			return this._active;
		}

		/**
		 * @implements DashboardStateAPI#setActive
		 */
		setActive(isActive) {
			this._active = isActive;
			this.invokeCallbacks(DashboardState.ON_DASHBOARD_STATE_CHANGE, {
				name: 'change:' + (this._active?'active':'deactive'),
				info: {
					actionName: 'setActive',
					value: this._active
				}
			});
		}

		/**
		 * @implements DashboardStateAPI#onActive
		 */
		onChangeActive(callBack){
			this.registerCallback(DashboardState.ON_DASHBOARD_STATE_CHANGE, callBack);
		}
	}
	return DashboardState;
});
